import { cn } from "cn";
import { motion } from "motion/react";

import { Badge } from "@/components/ui/badge/badge";
import { BwChatBubbleFill } from "@/components/ui/icons";
import { useDraggableBubble } from "@/features/shared/draggable-bubble";
import { useSupportChat } from "../_hooks/use-support-chat";
import {
  SUPPORT_BUBBLE_METRICS,
  SUPPORT_BUBBLE_STORAGE_KEY,
} from "../_libs/support-bubble-metrics";
import { useSupportChatState } from "../_libs/support-chat-store";

const PANEL_EASE = [0.4, 0, 0.2, 1] as const;

/**
 * The draggable support-chat launcher, replacing the vendor's fixed one.
 *
 * Mounted once per member surface, next to `<Outlet />` under the messenger
 * provider — the stock launcher was global to that whole route group, so this
 * reproduces its coverage exactly.
 *
 * Hidden rather than morphed into a close button while the messenger is open:
 * the messenger window stays pinned bottom-right while this bubble may be
 * parked anywhere, and a close button sitting far from the panel it closes
 * reads as broken.
 */
export function SupportChatBubble() {
  const { show, isOpen } = useSupportChat();
  const isReady = useSupportChatState((state) => state.isReady);
  const unreadCount = useSupportChatState((state) => state.unreadCount);

  // Before boot there is no messenger to open, and after `shutdown()` the
  // session is gone — in both cases the launcher must not be there to click.
  const visible = isReady && !isOpen;

  const { dragProps } = useDraggableBubble({
    metrics: SUPPORT_BUBBLE_METRICS,
    storageKey: SUPPORT_BUBBLE_STORAGE_KEY,
    enabled: visible,
    onActivate: show,
  });

  return (
    <motion.button
      type="button"
      initial={false}
      animate={{
        opacity: visible ? 1 : 0,
        scale: visible ? 1 : 0.92,
      }}
      transition={{
        opacity: { duration: 0.18 },
        scale: { duration: 0.22, ease: PANEL_EASE },
      }}
      {...dragProps}
      // `dragProps.style` carries the x/y motion values, so it must be spread
      // rather than replaced — a bare `style` here would freeze the bubble at
      // the viewport origin.
      style={{ ...dragProps.style }}
      className={cn(
        /* sorted */
        "fixed top-0 left-0 z-40 flex",
        "items-center justify-center",
        "size-12",
        "text-white",
        "bg-bg-primary",
        "rounded-full cursor-grab [-webkit-tap-highlight-color:transparent]",
        "active:cursor-grabbing",
        !visible && "pointer-events-none",
      )}
    >
      <BwChatBubbleFill className="size-6" />
      {unreadCount > 0 && (
        <Badge
          kind="solid"
          color="red"
          size="xs"
          // The count is the label; announcing it keeps the badge meaningful to
          // a screen reader, which cannot infer it from the colour.
          aria-label={`${unreadCount} unread`}
          className="absolute -top-1 -right-1 justify-center min-w-5"
        >
          {unreadCount > 99 ? "99+" : unreadCount}
        </Badge>
      )}
      <span className="sr-only">
        Open support chat. Drag to move, or use the arrow keys.
      </span>
    </motion.button>
  );
}
